'use client';

import { useEffect, useRef } from 'react';
import type { Service } from '@/lib/cms';
import { useCart } from '@/lib/cartContext';

interface ServicesSectionProps {
  services: Service[];
}

// Parse price string like "From ₹95" or "₹65" to a number
function parsePriceAmount(price: string): number {
  const match = price.replace(/,/g, '').match(/\d+/);
  return match ? parseInt(match[0], 10) : 0;
}

export default function ServicesSection({ services }: ServicesSectionProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const { addItem, removeItem, isInCart } = useCart();

  useEffect(() => {
    const items = sectionRef?.current?.querySelectorAll('.reveal');
    if (!items) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.remove('hidden-start');
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    items?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="services" className="py-24 bg-cream-dark" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="mb-14 max-w-2xl">
          <span className="text-xs font-mono text-muted uppercase tracking-widest block mb-3 border-b border-border w-fit pb-1 reveal hidden-start">
            02 — Services & Pricing
          </span>
          <h2 className="font-display text-4xl md:text-6xl font-medium text-primary tracking-tight leading-tight mb-4 reveal hidden-start reveal-delay-1">
            Transparent pricing,
            <br />
            <span className="italic text-accent-rose">zero surprises</span>
          </h2>
          <p className="text-muted text-base leading-relaxed reveal hidden-start reveal-delay-2">
            Every service includes a complimentary consultation. No hidden fees, no upselling — just
            exceptional work at a fair, clearly stated price.
          </p>
        </div>

        {/* Service Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {services?.map((service, i) => {
            const inCart = isInCart(service.id);
            return (
              <div
                key={service.id}
                className={`service-card relative rounded-4xl p-8 border border-border bg-surface reveal hidden-start overflow-hidden`}
                style={{ transitionDelay: `${i * 0.12}s` }}
              >
                {/* Badge */}
                {service?.badge && (
                  <div className="absolute top-5 right-5">
                    <span className="px-3 py-1 bg-accent text-white text-[10px] font-semibold uppercase tracking-widest rounded-full">
                      {service?.badge}
                    </span>
                  </div>
                )}

                {/* Accent background dot */}
                <div
                  className={`absolute -top-12 -right-12 w-40 h-40 rounded-full ${service?.accent} blur-2xl`}
                />

                {/* Content */}
                <div className="relative z-10">
                  <div className="mb-6">
                    <p className="text-xs font-mono text-muted uppercase tracking-widest mb-2">
                      {service?.tagline}
                    </p>
                    <h3 className="font-display text-2xl font-medium text-primary tracking-tight mb-3">
                      {service?.name}
                    </h3>
                    <div className="flex items-baseline gap-3">
                      <span className="font-display text-3xl font-semibold text-accent">
                        {service?.price}
                      </span>
                      <span className="text-sm text-muted">· {service?.duration}</span>
                    </div>
                  </div>

                  <p className="text-sm text-foreground-soft leading-relaxed mb-6">
                    {service?.description}
                  </p>

                  {/* Includes list */}
                  <div className="mb-8">
                    <p className="text-xs font-semibold text-primary uppercase tracking-wider mb-3">
                      Includes
                    </p>
                    <ul className="space-y-2">
                      {(service?.includes ?? [])?.map((item, j) => (
                        <li key={j} className="flex items-start gap-2.5 text-sm text-foreground-soft">
                          <svg
                            className="w-4 h-4 text-accent mt-0.5 flex-shrink-0"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M20 6L9 17l-5-5" />
                          </svg>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Action buttons */}
                  <div className="flex flex-col gap-2">
                    <a
                      href="#booking"
                      className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary text-white text-sm font-semibold rounded-full hover:bg-foreground-soft transition-all duration-300"
                    >
                      Book This Service
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M5 12h14m-7-7 7 7-7 7" />
                      </svg>
                    </a>

                    <button
                      onClick={() => {
                        if (inCart) {
                          removeItem(service.id);
                        } else {
                          addItem({
                            id: service.id,
                            name: service.name,
                            price: service.price,
                            priceAmount: parsePriceAmount(service.price),
                            duration: service.duration,
                            tagline: service.tagline,
                          });
                        }
                      }}
                      className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold rounded-full border transition-all duration-300 ${
                        inCart
                          ? 'bg-accent/10 border-accent text-accent hover:bg-accent/20' :'bg-transparent border-border text-foreground-soft hover:border-accent hover:text-accent'
                      }`}
                    >
                      {inCart ? (
                        <>
                          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 6L9 17l-5-5" />
                          </svg>
                          Added to Cart
                        </>
                      ) : (
                        <>
                          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                            <line x1="3" y1="6" x2="21" y2="6" />
                            <path d="M16 10a4 4 0 0 1-8 0" />
                          </svg>
                          Add to Cart
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Add-ons note */}
        <div className="mt-8 p-6 rounded-3xl border border-border bg-surface/60 flex flex-col md:flex-row items-start md:items-center gap-4 reveal hidden-start">
          <div className="w-10 h-10 rounded-2xl bg-accent/10 flex items-center justify-center flex-shrink-0">
            <svg
              className="w-5 h-5 text-accent"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 5v14m-7-7h14" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-semibold text-primary mb-1">Add-ons available</p>
            <p className="text-sm text-muted">
              Crystal embellishments (+$15), nail extensions (+$25), same-day booking (+$20 rush
              fee). All prices include removal of previous gel if needed.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
